import ExcelJS from 'exceljs'
import { listKeyResultsByOKR } from '@/api/performance'
import type { KeyResult } from '@/types/performance'

const COLORS = {
  headerDark: 'FF404040',
  white: 'FFFFFFFF',
  borderLight: 'FFE5E5E5',
}

const KR_STATUS_LABELS: Record<string, string> = {
  'not-started': 'Nao iniciado',
  'in-progress': 'Em andamento',
  'completed': 'Concluido',
  'at-risk': 'Em risco',
  'cancelled': 'Cancelado',
}

const OKR_STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  completed: 'Concluido',
  cancelled: 'Cancelado',
}

const BATCH_SIZE = 10

async function fetchAllKRs(
  tenantDomain: string,
  okrIds: string[]
): Promise<Record<string, KeyResult[]>> {
  const result: Record<string, KeyResult[]> = {}

  for (let i = 0; i < okrIds.length; i += BATCH_SIZE) {
    const batch = okrIds.slice(i, i + BATCH_SIZE)
    const responses = await Promise.all(
      batch.map(okrId =>
        listKeyResultsByOKR(tenantDomain, okrId)
          .then(r => ({ okrId, keyResults: r.keyResults }))
          .catch(() => ({ okrId, keyResults: [] as KeyResult[] }))
      )
    )
    responses.forEach(({ okrId, keyResults }) => {
      result[okrId] = keyResults
    })
  }

  return result
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

interface ExportParams {
  tenantDomain: string
  okrs: any[]
  statusFilter: string
}

export async function exportObjetivosToExcel({
  tenantDomain,
  okrs,
  statusFilter,
}: ExportParams): Promise<void> {
  // Fetch all KRs in batches
  const okrIds = okrs.map((o: any) => o.okrId)
  const krMap = await fetchAllKRs(tenantDomain, okrIds)

  // Build workbook
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'Monutrack'
  workbook.created = new Date()
  workbook.modified = new Date()

  const worksheet = workbook.addWorksheet('Objetivos Individuais', {
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true },
  })

  // Column widths
  worksheet.columns = [
    { width: 25 }, // Colaborador
    { width: 40 }, // Objetivo
    { width: 14 }, // Status Objetivo
    { width: 12 }, // Progresso
    { width: 12 }, // Nota Objetivo
    { width: 30 }, // Avaliadores
    { width: 14 }, // Periodo Inicio
    { width: 14 }, // Periodo Fim
    { width: 14 }, // KRs Avaliados
    { width: 35 }, // KR Nome
    { width: 35 }, // KR Descricao
    { width: 14 }, // KR Valor Atual
    { width: 14 }, // KR Valor Alvo
    { width: 10 }, // KR Unidade
    { width: 10 }, // KR Peso
    { width: 16 }, // KR Status
  ]

  // Title row
  const titleRow = worksheet.addRow(['Objetivos Individuais — Exportacao'])
  titleRow.font = { size: 14, bold: true }
  titleRow.alignment = { vertical: 'middle', horizontal: 'center' }
  worksheet.mergeCells(1, 1, 1, 16)

  // Info row
  const infoRow = worksheet.addRow([
    `Gerado em ${new Date().toLocaleString('pt-BR')} | Filtro: ${statusFilter === 'all' ? 'Todos' : OKR_STATUS_LABELS[statusFilter] || statusFilter} | Total: ${okrs.length} objetivos`,
  ])
  infoRow.font = { size: 10 }
  infoRow.alignment = { vertical: 'middle', horizontal: 'center' }
  worksheet.mergeCells(2, 1, 2, 16)

  // Empty row
  worksheet.addRow([])

  // Header row
  const headers = [
    'Colaborador',
    'Objetivo',
    'Status Objetivo',
    'Progresso (%)',
    'Nota Objetivo',
    'Avaliadores',
    'Periodo Inicio',
    'Periodo Fim',
    'KRs Avaliados',
    'KR Nome',
    'KR Descricao',
    'KR Valor Atual',
    'KR Valor Alvo',
    'KR Unidade',
    'KR Peso',
    'KR Status',
  ]
  const headerRow = worksheet.addRow(headers)
  headerRow.font = { bold: true, color: { argb: COLORS.white } }
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.headerDark },
  }
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' }
  headerRow.height = 25

  // Freeze header
  worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }]

  // Data rows — one row per KR, or one row per OKR if no KRs
  for (const okr of okrs) {
    const krs = krMap[okr.okrId] || []
    const ownerName = okr.ownerName || ''
    const objective = okr.objective || ''
    const statusLabel = OKR_STATUS_LABELS[okr.status] || okr.status
    const progress = okr.progress || 0
    const score = okr.objectiveScore != null ? Number(okr.objectiveScore.toFixed(1)) : null
    const evaluators = okr.evaluators?.map((e: any) => e.name).join(', ') || ''
    const periodStart = formatDate(okr.period?.startDate)
    const periodEnd = formatDate(okr.period?.endDate)
    const krsEval = `${okr.scoredKRs ?? 0}/${okr.totalKRs ?? 0}`

    if (krs.length === 0) {
      worksheet.addRow([
        ownerName, objective, statusLabel, progress, score,
        evaluators, periodStart, periodEnd, krsEval,
        '', '', null, null, '', null, '',
      ])
    } else {
      for (const kr of krs) {
        worksheet.addRow([
          ownerName, objective, statusLabel, progress, score,
          evaluators, periodStart, periodEnd, krsEval,
          kr.name || '',
          kr.description || '',
          kr.currentValue ?? null,
          kr.targetValue ?? null,
          kr.unit || '',
          kr.weight ?? null,
          KR_STATUS_LABELS[kr.status] || kr.status || '',
        ])
      }
    }
  }

  // Apply borders
  const lastRow = worksheet.rowCount
  for (let i = 4; i <= lastRow; i++) {
    const row = worksheet.getRow(i)
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: COLORS.borderLight } },
        left: { style: 'thin', color: { argb: COLORS.borderLight } },
        bottom: { style: 'thin', color: { argb: COLORS.borderLight } },
        right: { style: 'thin', color: { argb: COLORS.borderLight } },
      }
    })
  }

  // Download
  const date = new Date().toISOString().split('T')[0]
  const filterSuffix = statusFilter !== 'all' ? `-${statusFilter}` : ''
  const fileName = `objetivos-individuais${filterSuffix}-${date}.xlsx`

  const buffer = await workbook.xlsx.writeBuffer()
  downloadFile(buffer as ArrayBuffer, fileName)
}

function downloadFile(buffer: ArrayBuffer, fileName: string) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
